import useSWR from 'swr'
import { ChangeEvent, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { Employee, TeamOverviewFiltersCollection } from '@/interfaces/schema'
import { fetchData } from '@/services/contentful/api/getData'
import { ourTeamEmployees } from '@/services/contentful/queries/ourTeam'
import { EmployeeCollectionQuery } from '@/interfaces/employee'

export const ourTeamInit: { searchQuery: string } = {
  searchQuery: '',
}

export interface UseOurTeamOutput {
  handleCheckBoxChange: (e: ChangeEvent<HTMLInputElement>) => void
  handleToggleFilters: (e: ChangeEvent<HTMLInputElement>) => void
  activeFilters: string[]
  filteredEmployees: Employee[] | null
  loading: boolean
  errorMessage: string
  isErrorSnackbarOpen: boolean
  handleCloseErrorSnackbar: () => void
  handleInputSearchQueryChange: (e: ChangeEvent<HTMLInputElement>) => void
  handleSubmitSearch: () => void
  handleLoadMore: (e: React.MouseEvent<HTMLButtonElement>) => void
  totalCount: number
}

const useOurTeam = ({
  filtersCollection,
}: {
  filtersCollection?: TeamOverviewFiltersCollection
}): UseOurTeamOutput => {
  const { push, query } = useRouter()

  const getInitActiveFilters = (): string[] => {
    if (query.filters) {
      const parsedFilters = JSON.parse(query.filters as string)
      if (Array.isArray(parsedFilters) && parsedFilters.length)
        return parsedFilters
    }
    return filtersCollection?.items.map(({ teamName }) => teamName ?? '') ?? []
  }

  const [employeeList, setEmployeeList] = useState<Employee[] | null>(null)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [numToSkip, setNumToSkip] = useState<number>(0)
  const [doFetch, setDoFetch] = useState<boolean>(true)
  // doFetch state is used to trigger SWR fetch - we use this, because the fetch happens on render OR onClick (in case of search)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [activeFilters, setFilters] = useState<string[]>(getInitActiveFilters())
  const [isErrorSnackbarOpen, setErrorSnackbarOpen] = useState<boolean>(false)
  const handleCloseErrorSnackbar = () => setErrorSnackbarOpen(false)

  // handling filters and router query
  const pushFilterToQuery = (filters: string[]) => {
    push({ query: { ...query, filters: JSON.stringify(filters) } }, undefined, {
      shallow: true,
    })
  }

  const handleCheckBoxChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { name } = evt.target

    setFilters((prev) => {
      let newFilters: string[]
      if (activeFilters.includes(name)) {
        newFilters = [...prev].filter((single) => single !== name)
      } else {
        newFilters = [...prev, name]
      }

      pushFilterToQuery(newFilters)
      return newFilters
    })
  }

  const handleToggleFilters = (evt: ChangeEvent<HTMLInputElement>) => {
    evt.preventDefault()
    const isChecked = evt.target.checked
    let newFilters: string[]

    if (!isChecked) {
      newFilters = []
    } else {
      newFilters =
        filtersCollection?.items.map(({ teamName }) => teamName ?? '') ?? []
    }

    pushFilterToQuery(newFilters)
    setFilters(newFilters)
  }

  // handling search and fetching of employees
  const handleInputSearchQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setDoFetch(false)
    setSearchQuery(value)
  }

  const handleLoadMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (employeeList?.length === totalCount) return
    setNumToSkip(employeeList?.length ?? 0)
  }

  const limit = !numToSkip ? 17 : 18 // inital limit is 17 because a grid spot is taken by CTA card, limit is 18 to fill a 3x6 grid

  const { data, error } = useSWR<EmployeeCollectionQuery, Error>(
    !doFetch
      ? null
      : ourTeamEmployees(searchQuery, numToSkip, limit, activeFilters),
    fetchData
  )

  if (error) {
    setErrorMessage(
      error?.message ?? "Oops! We kunnen momenteel de teamfoto's niet laden..."
    )
    setErrorSnackbarOpen(true)
  }

  const loading = !employeeList && !error

  const handleSubmitSearch = () => {
    setDoFetch(true)
  }

  useEffect(() => {
    if (!data || !doFetch) return

    setTotalCount(data?.employeeCollection?.total)
    setEmployeeList((prevItems) => {
      const newItems = data?.employeeCollection?.items ?? []
      if (numToSkip) {
        return [...(prevItems || []), ...newItems]
      } else {
        return newItems
      }
    })
  }, [data, doFetch])

  return {
    handleCheckBoxChange,
    handleToggleFilters,
    activeFilters,
    filteredEmployees: employeeList ?? null,
    loading,
    errorMessage,
    isErrorSnackbarOpen,
    handleCloseErrorSnackbar,
    handleInputSearchQueryChange,
    handleSubmitSearch,
    handleLoadMore,
    totalCount,
  }
}

export default useOurTeam
